import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Header from '../Components/Header';
import Estimate from '../Components/Estimate';
import SlideMenu from '../Components/SlideMenu';
import AuctionChart from '../Components/AuctionChart';
import { getAuctionStats, formatPrice, calculateAuctionStats } from '../services/auctionService';
import { db } from '../firebase';
import { collection, query, where, getDocs, doc, onSnapshot } from 'firebase/firestore';
import './CarPage.css';
import carImage from '../assets/images/72_911.png';
import { useAuth, useUser } from "@clerk/react";
import useSavedCars from '../hooks/useSavedCars';
import MileageEstimator from '../Components/MileageEstimator';
import ConditionEstimator from '../Components/ConditionEstimator';
import Footer from '../Components/Footer';
import AdvertisePopup from '../Components/AdvertisePopup';

const CarPage = () => {
    const { modelId } = useParams();
    const { has, userId } = useAuth();
    const { user } = useUser();
    
    // Check if user has the bidder_iq plan via Clerk Billing OR user metadata
    const hasPaidPlan = (has?.({ plan: 'bidder_iq' }) ?? false) || user?.publicMetadata?.plan === 'bidder_iq';
    const { isSaved, toggleSavedCar } = useSavedCars(hasPaidPlan ? userId : null);
    const [showEstimate, setShowEstimate] = useState(false);
    const [auctionStats, setAuctionStats] = useState(null);  
    const [loading, setLoading] = useState(true);
    const [auctionData, setAuctionData] = useState([]);
    const [timeframe, setTimeframe] = useState('12 Months');
    const [sortBy, setSortBy] = useState('miles');  
    const [modelData, setModelData] = useState(null);
    const [modelNotFound, setModelNotFound] = useState(false);
    const [filters, setFilters] = useState({
        year: new Set(),
        colorFamily: new Set(),
        intColorFamily: new Set(),
        transmission: new Set(),
        options: new Set(),
        accident: new Set(),
        condition: new Set(),
        conditionType: new Set(),
        engine: new Set(),
        region: new Set(),
        owners: new Set(),
        modifier1: new Set(),
        modifier2: new Set(),
        modifier3: new Set(),
        modifier4: new Set(),
        modifier5: new Set(),
        auctionHouse: new Set(),
        auctionPlatform: new Set(),
        soldStatus: new Set()
    });
    const [uniqueValues, setUniqueValues] = useState({
        year: [],
        colorFamily: [],
        intColorFamily: [],
        transmission: [],
        options: [],
        accident: [],
        condition: [],
        conditionType: [],
        engine: [],
        region: [],
        owners: [],
        modifier1: [],
        modifier2: [],
        modifier3: [],
        modifier4: [],
        modifier5: [],
        auctionHouse: [],
        auctionPlatform: [],
        soldStatus: []
    });
    const [sortOption, setSortOption] = useState("Date: New to Old");
    const [auctionViewMode, setAuctionViewMode] = useState('results');
    const [hoveredAuction, setHoveredAuction] = useState(null);
    const [hoveredFilter, setHoveredFilter] = useState({ category: null, value: null });
    const [advertisers, setAdvertisers] = useState([]);
    const [previousPeriodStats, setPreviousPeriodStats] = useState(null);
    const [showConditionBands, setShowConditionBands] = useState(false);
    const [conditionBands, setConditionBands] = useState([]);
    const [showMileageBands, setShowMileageBands] = useState(false);
    const [mileageRegression, setMileageRegression] = useState(null);

    const categoryDisplayNames = {
        colorFamily: 'Exterior Color',
        intColorFamily: 'Interior Color',
        options: 'Notable Options/Mods',
        region: 'Region',
        owners: 'One-Owner',
        modifier1: 'Modifier',
        modifier2: 'Modifier',
        modifier3: 'Modifier',
        modifier4: 'Modifier',
        modifier5: 'Modifier',
        accident: 'Accident History',
        condition: 'Condition',
        conditionType: 'Condition Type',
        engine: 'Engine',
        auctionPlatform: 'Auction Platform'
    };

    // Function to get display value for filter options
    const getDisplayValue = (category, value) => {
        if (category === 'accident') {
            return value === 'TRUE' ? 'Yes' : 'No';
        }
        if (category === 'transmission') {
            return value.charAt(0).toUpperCase() + value.slice(1);
        }
        return value;
    };

    // Function to filter data based on timeframe
    const filterDataByTimeframe = (data, selectedTimeframe) => {
        const now = new Date();
        let startDate;
        
        switch (selectedTimeframe) {
            case '24 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
                break;
            case '12 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                break;
            case '6 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '3 Months':
                startDate = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                break;
            case '1 Month':
                startDate = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                break;
            default:
                startDate = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
        }

        return data.filter(auction => {
            const auctionDate = auction.auctionDate?.toDate?.() || new Date(auction.auctionDate);
            return auctionDate >= startDate;
        });
    };

    // Function to filter data for the previous equivalent period
    const filterPreviousPeriod = (data, selectedTimeframe) => {
        const now = new Date();
        let currentStart, previousStart;
        
        switch (selectedTimeframe) {
            case '24 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 48, now.getDate());
                break;
            case '12 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
                break;
            case '6 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                break;
            case '3 Months':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 3, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 6, now.getDate());
                break;
            case '1 Month':
                currentStart = new Date(now.getFullYear(), now.getMonth() - 1, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 2, now.getDate());
                break;
            default:
                currentStart = new Date(now.getFullYear(), now.getMonth() - 12, now.getDate());
                previousStart = new Date(now.getFullYear(), now.getMonth() - 24, now.getDate());
        }

        return data.filter(auction => {
            const auctionDate = auction.auctionDate?.toDate?.() || new Date(auction.auctionDate);
            return auctionDate >= previousStart && auctionDate < currentStart;
        });
    };

    // Function to calculate stats from filtered data
    const calculateStats = (data) => {
        let totalPrice = 0;
        let highestPrice = 0;
        let lowestPrice = Infinity;
        let soldCount = 0;

        data.forEach(auction => {
            const finalBid = parseFloat(auction.finalBid);
            if (auction.sold) {
                soldCount++;
                totalPrice += finalBid;
                highestPrice = Math.max(highestPrice, finalBid);
                lowestPrice = Math.min(lowestPrice, finalBid);
            }
        });

        return {
            sellThroughRate: data.length > 0 ? Number(((soldCount / data.length) * 100).toFixed(1)) : 0,
            averagePrice: soldCount > 0 ? totalPrice / soldCount : 0,
            highestPrice: highestPrice,
            lowestPrice: lowestPrice === Infinity ? 0 : lowestPrice
        };
    };

    // Function to get unique values and filter out categories with single/no values
    const getUniqueValues = (data, field) => {
        if (!data || data.length === 0) return [];
        
        if (field === 'soldStatus') {
            const values = new Set();
            data.forEach(item => {
                values.add(item.sold ? 'Sold' : 'Not Sold');
            });
            // Only return if there are both sold and unsold items
            return values.size > 1 ? Array.from(values).sort() : [];
        }

        if (field === 'options') {
            const values = new Set();
            data.forEach(item => {
                if (Array.isArray(item.options)) {
                    item.options.forEach(option => {
                        if (option && option.trim()) {
                            values.add(option.trim());
                        }
                    });
                }
            });
            // Only return if there are multiple options
            return values.size > 1 ? Array.from(values).sort() : [];
        }
        
        if (field === 'accident') {
            const values = new Set();
            data.forEach(item => {
                const value = item[field]?.toString();
                if (value) values.add(value);
            });
            // Debug log for boolean fields
            console.log(`${field} values:`, {
                values: Array.from(values),
                hasTrue: values.has('TRUE'),
                hasFalse: values.has('FALSE'),
                willShow: values.has('TRUE') && values.has('FALSE')
            });
            // For boolean fields, only show if both TRUE and FALSE exist
            return values.has('TRUE') && values.has('FALSE') ? Array.from(values).sort() : [];
        }
        
        // For all other fields
        const values = new Set();
        data.forEach(item => {
            const value = item[field]?.toString();
            if (value) values.add(value);
        });
        
        // Only return if there are multiple values
        return values.size > 1 ? Array.from(values).sort() : [];
    };

    // Function to initialize filters with all values checked
    const initializeFilters = (data) => {
        const uniqueValuesObj = {};
        Object.keys(uniqueValues).forEach(field => {
            const values = getUniqueValues(data, field);
            // Only include fields that have multiple values
            if (values.length > 0) {
                uniqueValuesObj[field] = values;
            }
        });
        setUniqueValues(uniqueValuesObj);

        // Initialize all filters as checked
        const newFilters = {};
        Object.keys(uniqueValuesObj).forEach(field => {
            newFilters[field] = new Set(uniqueValuesObj[field]);
        });
        setFilters(newFilters);
    };

    // Function to apply filters to previous period data
    // Only excludes values the user explicitly unchecked (present in uniqueValues but not in filters)
    // Values that don't exist in the current period's uniqueValues are allowed through
    const applyFiltersForPreviousPeriod = (data) => {
        return data.filter(item => {
            return Object.keys(filters).every(field => {
                if (filters[field].size === 0) return true;
                if (field === 'soldStatus') return true;

                if (field === 'options') {
                    if (!Array.isArray(item.options) || item.options.length === 0) return true;
                    return item.options.every(option => {
                        const isKnownValue = uniqueValues[field]?.includes(option);
                        if (!isKnownValue) return true;
                        return filters[field].has(option);
                    });
                }

                const itemValue = item[field]?.toString();
                if (!itemValue) return true;
                const isKnownValue = uniqueValues[field]?.includes(itemValue);
                if (!isKnownValue) return true;
                return filters[field].has(itemValue);
            });
        });
    };

    // Function to apply filters to data
    const applyFilters = (data, includeSoldFilter = true) => {
        return data.filter(item => {
            return Object.keys(filters).every(field => {
                if (filters[field].size === 0) return true;
                
                if (field === 'soldStatus') {
                    if (!includeSoldFilter) return true;
                    const soldValue = item.sold ? 'Sold' : 'Not Sold';
                    return filters[field].has(soldValue);
                }
                
                if (field === 'options') {
                    // If item has no options, only include if this filter isn't being used
                    if (!Array.isArray(item.options) || item.options.length === 0) {
                        return true; // Include items without options
                    }
                    
                    // For items with options, ALL of their options must be selected
                    // If any option the item has is unchecked, filter it out
                    return item.options.every(option => 
                        filters[field].has(option)
                    );
                }
                
                const itemValue = item[field]?.toString();
                // If the item doesn't have this field, include it (don't filter it out)
                if (!itemValue) return true;
                return filters[field].has(itemValue);
            });
        });
    };

    // Function to handle filter changes
    const handleFilterChange = (category, value, checked) => {
        setFilters(prev => {
            const newFilters = { ...prev };
            const newSet = new Set(prev[category]);
            
            if (checked) {
                newSet.add(value);
            } else {
                newSet.delete(value);
            }
            
            newFilters[category] = newSet;
            return newFilters;
        });
    };

    // Function to format mileage with commas
    const formatMileage = (miles) => {
        if (!miles) return '0';
        // Extract only numbers from the string
        const numericValue = miles.toString().replace(/[^0-9]/g, '');
        // Add commas to the number
        return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    };

    // Function to check if a filter value matches the hovered auction
    const isHighlighted = (category, value) => {
        if (!hoveredAuction) return false;
        
        if (category === 'soldStatus') {
            const soldValue = hoveredAuction.sold ? 'Sold' : 'Not Sold';
            return soldValue === value;
        }

        if (category === 'accident') {
            const accidentValue = hoveredAuction[category]?.toString();
            return accidentValue === value;
        }

        if (category === 'options') {
            return Array.isArray(hoveredAuction.options) && 
                   hoveredAuction.options.includes(value);
        }
        
        return hoveredAuction[category]?.toString() === value;
    };

    const completedAuctions = useMemo(() => {
        return auctionData.filter(a => a.finalBid != null && a.finalBid !== '');
    }, [auctionData]);

    const liveAuctions = useMemo(() => {
        return auctionData.filter(a => a.finalBid == null || a.finalBid === '');
    }, [auctionData]);

    // Get available values for the current timeframe
    const getTimeframeValues = useMemo(() => {
        if (!completedAuctions.length) return {};
        
        const timeframeData = filterDataByTimeframe(completedAuctions, timeframe);
        const fieldsToCheck = Object.keys(uniqueValues);
        const timeframeValues = {};
        
        fieldsToCheck.forEach(field => {
            if (field === 'soldStatus') {
                timeframeValues[field] = new Set(
                    timeframeData.map(item => item.sold ? 'Sold' : 'Not Sold')
                );
            } else if (field === 'options') {
                // Handle options as an array
                timeframeValues[field] = new Set();
                timeframeData.forEach(item => {
                    if (Array.isArray(item.options)) {
                        item.options.forEach(option => {
                            if (option && option.trim()) {
                                timeframeValues[field].add(option.trim());
                            }
                        });
                    }
                });
            } else {
                timeframeValues[field] = new Set(
                    timeframeData.map(item => item[field]?.toString()).filter(Boolean)
                );
            }
        });
        
        return timeframeValues;
    }, [completedAuctions, timeframe, uniqueValues]);

    const filteredData = useMemo(() => {
        const timeframeFiltered = filterDataByTimeframe(completedAuctions, timeframe);
        return applyFilters(timeframeFiltered);
    }, [completedAuctions, timeframe, filters]);

    // Stable normalized array for MileageEstimator. Without this memo, every
    // CarPage render produced a new array reference, retriggering the
    // estimator's regression useMemo, which called setMileageRegression and
    // caused another CarPage render — a tight loop that made the page sluggish.
    const mileageEstimatorAuctions = useMemo(() => (
        filteredData.map(a => ({
            ...a,
            miles: typeof a.miles === 'number'
                ? a.miles
                : parseInt(String(a.miles).replace(/[^0-9]/g, ''), 10) || 0,
            price: typeof a.finalBid === 'number'
                ? a.finalBid
                : parseFloat(a.finalBid) || 0
        }))
    ), [filteredData]);

    const sortedAuctionData = useMemo(() => {
        if (!filteredData) return [];
        
        const dataToSort = [...filteredData];
        
        switch (sortOption) {
            case "Date: New to Old":
                return dataToSort.sort((a, b) => {
                    const dateA = a.auctionDate?.toDate?.() || new Date(a.auctionDate);
                    const dateB = b.auctionDate?.toDate?.() || new Date(b.auctionDate);
                    return dateB - dateA;
                });
            case "Date: Old to New":
                return dataToSort.sort((a, b) => {
                    const dateA = a.auctionDate?.toDate?.() || new Date(a.auctionDate);
                    const dateB = b.auctionDate?.toDate?.() || new Date(b.auctionDate);
                    return dateA - dateB;
                });
            case "Price: High to Low":
                return dataToSort.sort((a, b) => parseFloat(b.finalBid) - parseFloat(a.finalBid));
            case "Price: Low to High":
                return dataToSort.sort((a, b) => parseFloat(a.finalBid) - parseFloat(b.finalBid));
            case "Miles: High to Low":
                return dataToSort.sort((a, b) => {
                    const milesA = parseFloat(a.miles?.replace(/[^0-9]/g, '')) || 0;
                    const milesB = parseFloat(b.miles?.replace(/[^0-9]/g, '')) || 0;
                    return milesB - milesA;
                });
            case "Miles: Low to High":
                return dataToSort.sort((a, b) => {
                    const milesA = parseFloat(a.miles?.replace(/[^0-9]/g, '')) || 0;
                    const milesB = parseFloat(b.miles?.replace(/[^0-9]/g, '')) || 0;
                    return milesA - milesB;
                });
            default:
                return dataToSort;
        }
    }, [filteredData, sortOption]);

    useEffect(() => {
        const fetchModelData = async () => {
            try {
                // Fetch model data from modelLines collection
                const modelQuery = query(
                    collection(db, 'modelLines'),
                    where('modelId', '==', modelId)
                );
                const modelSnapshot = await getDocs(modelQuery);

                if (modelSnapshot.empty) {
                    setModelNotFound(true);
                    setLoading(false);
                    return;
                }

                const modelData = modelSnapshot.docs[0].data();
                setModelData(modelData);

                // Set up real-time listener for auction data
                const auctionsRef = collection(db, 'auctions');
                const q = query(auctionsRef, where('modelId', '==', modelId));
                
                const unsubscribe = onSnapshot(q, (querySnapshot) => {
                    const auctionDataArray = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));

                    console.log('=== REAL-TIME DATA UPDATE ===');
                    console.log('Total auctions fetched from Firebase:', auctionDataArray.length);
                    console.log('ModelId:', modelId);
                    console.log('Fetched auction data (real-time):', auctionDataArray);
                    console.log('Sample options from first few auctions:', 
                        auctionDataArray.slice(0, 5).map(auction => ({
                            type: typeof auction.options,
                            isArray: Array.isArray(auction.options),
                            value: auction.options
                        }))
                    );

                    setAuctionData(auctionDataArray);
                    initializeFilters(auctionDataArray);
                    setLoading(false);
                }, (error) => {
                    console.error('Error fetching auction data:', error);
                    setLoading(false);
                });

                // Return cleanup function to unsubscribe when component unmounts
                return unsubscribe;
            } catch (error) {
                console.error('Error fetching model data:', error);
                setLoading(false);
            }
        };

        const unsubscribe = fetchModelData();
        
        // Cleanup function
        return () => {
            if (unsubscribe && typeof unsubscribe.then === 'function') {
                unsubscribe.then(unsub => unsub && unsub());
            }
        };
    }, [modelId]);

    // Fetch advertisers
    useEffect(() => {
        const fetchAdvertisers = async () => {
            try {
                const advertisersRef = collection(db, 'advertisers');
                const advertiserSnapshot = await getDocs(advertisersRef);
                const ads = advertiserSnapshot.docs.map(doc => ({
                    id: doc.id,
                    ...doc.data()
                }));
                // Shuffle advertisers randomly
                const shuffled = [...ads].sort(() => Math.random() - 0.5);
                setAdvertisers(shuffled);
            } catch (error) {
                console.error('Error fetching advertisers:', error);
            }
        };
        fetchAdvertisers();
    }, []);

    useEffect(() => {
        if (completedAuctions && completedAuctions.length > 0) {
            const timeframeData = filterDataByTimeframe(completedAuctions, timeframe);
            console.log('Timeframe data:', {
                timeframe,
                totalCount: timeframeData.length,
                accidentTrue: timeframeData.filter(item => item.accident === true).length,
                accidentFalse: timeframeData.filter(item => item.accident === false).length,
                conditionTypes: [...new Set(timeframeData.map(item => item.conditionType))].filter(Boolean)
            });
            
            // Use timeframeData directly instead of applying filters for stats calculation
            // This ensures stats reflect all data in the selected timeframe
            const stats = calculateStats(timeframeData);
            setAuctionStats(stats);

            // Update uniqueValues for the new timeframe
            const uniqueValuesObj = {};
            const fieldsToCheck = [
                'year', 'colorFamily', 'intColorFamily', 'transmission', 
                'options', 'accident', 'condition', 'conditionType', 'engine', 
                'region', 'owners', 'modifier1', 'modifier2', 'modifier3', 'modifier4', 'modifier5',
                'auctionHouse', 'auctionPlatform', 'soldStatus'
            ];
            
            fieldsToCheck.forEach(field => {
                const values = getUniqueValues(timeframeData, field);
                // Only include fields that have multiple values
                if (values.length > 0) {
                    uniqueValuesObj[field] = values;
                }
            });
            
            console.log('Updated uniqueValues:', uniqueValuesObj);
            setUniqueValues(uniqueValuesObj);

            // Update filters to only include values that exist in the new timeframe
            setFilters(prev => {
                const newFilters = {};
                Object.keys(uniqueValuesObj).forEach(field => {
                    newFilters[field] = new Set(uniqueValuesObj[field]);
                });
                return newFilters;
            });
        }
    }, [completedAuctions, timeframe]);

    useEffect(() => {
        if (completedAuctions && completedAuctions.length > 0) {
            const timeframeData = filterDataByTimeframe(completedAuctions, timeframe);
            const filteredTimeframeData = applyFilters(timeframeData, false);
            const stats = calculateStats(filteredTimeframeData);
            setAuctionStats(stats);

            // Calculate previous period stats with user's unchecked filters applied
            const prevData = filterPreviousPeriod(completedAuctions, timeframe);
            const filteredPrevData = applyFiltersForPreviousPeriod(prevData);
            const prevStats = calculateStats(filteredPrevData);
            prevStats.unitVolume = filteredPrevData.length;
            setPreviousPeriodStats(prevStats);
        }
    }, [filters, completedAuctions, timeframe]);

    const handleTimeframeChange = (e) => {
        const newTimeframe = e.target.value;
        setTimeframe(newTimeframe);
    };

    // If model not found, redirect to home page
    if (modelNotFound) {
        return <Navigate to="/" />;
    }

    // If still loading, show loading state
    if (loading) {
        return (
            <div className="betasml-carpage">
                <Header />
                {/* <SlideMenu /> */}
                <main className="carpage-content">
                    <div>Loading auction data...</div>
                </main>
            </div>
        );
    }

    // Fallback stats so the page still renders when there is no auction data yet
    const safeStats = auctionStats || {
        sellThroughRate: 0,
        averagePrice: 0,
        lowestPrice: 0,
        highestPrice: 0,
        totalVolume: 0
    };

    const handleEstimateClick = () => {
        setShowEstimate(true);
    };

    const handleClose = () => {
        setShowEstimate(false);
    };

    return (
        <div className="betasml-carpage">
            <Header />
            {/* <SlideMenu /> */}
            <main className="carpage-content">
                {/* Left Panel */}
                <section className="left-panel">
                    <div className="model-tile">
                        <div className="model-image-container">
                            <img 
                                src={modelData?.imageLink || carImage} 
                                alt={modelData ? `${modelData.modelLine} ${modelData.model}` : 'Loading...'} 
                                className="model-image"
                                loading="lazy"
                            />
                        </div>
                        <div className="model-info">
                            <h3>{modelData ? `${modelData.modelLine} ${modelData.model}` : 'Loading...'}</h3>
                            <p>({modelData?.year || '...'})</p>
                        </div>
                        {hasPaidPlan && (
                            <button 
                                className="save-star-btn"
                                onClick={() => toggleSavedCar(modelId)}
                                aria-label={isSaved(modelId) ? 'Unsave car' : 'Save car'}
                            >
                                {isSaved(modelId) ? (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#32C5FF" stroke="#32C5FF" strokeWidth="2">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                ) : (
                                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#99E2FF" strokeWidth="1.5">
                                        <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
                                    </svg>
                                )}
                            </button>
                        )}
                    </div>
                    
                    {/* Mileage Estimator - only show for users with paid plan */}
                    {hasPaidPlan && modelData?.estimator === 'mileage' && (
                        <MileageEstimator 
                            onGraphToggleChange={setShowMileageBands}
                            onRegressionChange={setMileageRegression}
                            filteredAuctions={mileageEstimatorAuctions}
                        />
                    )}

                    {hasPaidPlan && modelData?.estimator === 'condition' && (
                        <ConditionEstimator
                            filteredAuctions={filteredData}
                            onGraphToggleChange={setShowConditionBands}
                            onBandsChange={setConditionBands}
                        />
                    )}

                    {/* Trend Analysis tile - only show for users with paid plan */}
                    {hasPaidPlan && !(timeframe === '12 Months' || timeframe === '6 Months' || timeframe === '3 Months' || timeframe === '1 Month') && (
                        <div className="trend-analysis-tile">
                            <h3>Trend Analysis</h3>
                            <p className="trend-subtitle">
                                Select a shorter time period for Trend Analysis
                            </p>
                        </div>
                    )}
                    {hasPaidPlan && (timeframe === '12 Months' || timeframe === '6 Months' || timeframe === '3 Months' || timeframe === '1 Month') && (() => {
                        const hasPreviousPeriodData = (previousPeriodStats?.unitVolume || 0) > 0;
                        const volDelta = filteredData.length - (previousPeriodStats?.unitVolume || 0);
                        const strDelta = (auctionStats?.sellThroughRate || 0) - (previousPeriodStats?.sellThroughRate || 0);
                        const avgDelta = (auctionStats?.averagePrice || 0) - (previousPeriodStats?.averagePrice || 0);
                        const lowDelta = (auctionStats?.lowestPrice || 0) - (previousPeriodStats?.lowestPrice || 0);
                        const highDelta = (auctionStats?.highestPrice || 0) - (previousPeriodStats?.highestPrice || 0);

                        const deltaColor = (val) => val > 0 ? '#2ecda7' : val < 0 ? '#e85d75' : '#888';
                        const deltaSign = (val) => val > 0 ? '+' : '';
                        const renderDelta = (content, value) => {
                            if (!hasPreviousPeriodData) {
                                return <p className="trend-delta" style={{ color: '#888' }}>--</p>;
                            }

                            return (
                                <p className="trend-delta" style={{ color: deltaColor(value) }}>
                                    {content}
                                </p>
                            );
                        };

                        return (
                            <div className="trend-analysis-tile">
                                <h3>Trend Analysis</h3>
                                <p className="trend-subtitle">
                                    {hasPreviousPeriodData ? 'Compared to Previous Period' : 'No previous-period data yet'}
                                </p>
                                <div className="trend-stat-row">
                                    <p className="stat-title">Unit Volume</p>
                                    {renderDelta(`${deltaSign(volDelta)}${volDelta}`, volDelta)}
                                </div>
                                <div className="trend-stat-row">
                                    <p className="stat-title">Sell-Through Rate</p>
                                    {renderDelta(`${deltaSign(strDelta)}${Number(strDelta.toFixed(1))}%`, strDelta)}
                                </div>
                                <div className="trend-stat-row">
                                    <p className="stat-title">Average Price</p>
                                    {renderDelta(`${avgDelta < 0 ? '-' : avgDelta > 0 ? '+' : ''}${formatPrice(Math.abs(avgDelta))}`, avgDelta)}
                                </div>
                                <div className="trend-stat-row">
                                    <p className="stat-title">Low</p>
                                    {renderDelta(`${lowDelta < 0 ? '-' : lowDelta > 0 ? '+' : ''}${formatPrice(Math.abs(lowDelta))}`, lowDelta)}
                                </div>
                                <div className="trend-stat-row">
                                    <p className="stat-title">High</p>
                                    {renderDelta(`${highDelta < 0 ? '-' : highDelta > 0 ? '+' : ''}${formatPrice(Math.abs(highDelta))}`, highDelta)}
                                </div>
                            </div>
                        );
                    })()}
                    
                    {/* Advertiser Tiles - only show for users without paid plan */}
                    {!hasPaidPlan && advertisers.slice(0, 3).map((advertiser, index) => (
                        <a 
                            key={advertiser.id || index}
                            className="advertiser-tile"
                            href={advertiser.link}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            <div className="advertiser-logo-container">
                                <img 
                                    src={advertiser.logo} 
                                    alt={advertiser.companyName}
                                    className="advertiser-logo"
                                />
                            </div>
                            <div className="advertiser-info">
                                <h3>{advertiser.companyName}</h3>
                                <p>{advertiser.copy}</p>
                            </div>
                        </a>
                    ))}
                </section>

                {/* Right Section */}
                <section className="right-section">
                    <div className="dropdown-container">
                        <div className="toggle-container" style={{
                            display: 'flex',
                            alignItems: 'center',
                            marginRight: '15px'
                        }}>
                            <span style={{ marginRight: '8px' }}>Date</span>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={sortBy === 'miles'}
                                    onChange={(e) => setSortBy(e.target.checked ? 'miles' : 'date')}
                                />
                                <span className="slider round"></span>
                            </label>
                            <span style={{ marginLeft: '8px' }}>Miles</span>
                        </div>
                        <select
                            className="timeframe-select"
                            value={timeframe}
                            onChange={handleTimeframeChange}
                        >
                            <option>24 Months</option>
                            <option>12 Months</option>
                            <option>6 Months</option>
                            <option>3 Months</option>
                            <option>1 Month</option>
                        </select>
                    </div>
                    <div className="placeholder-chart-container">
                        {loading ? (
                            <p>Loading auction data...</p>
                        ) : (
                            <>
                                <div className="chart-legend">
                                    <div className="legend-item">
                                        <div className="legend-dot sold"></div>
                                        <span>Sold</span>
                                    </div>
                                    <div className="legend-item">
                                        <div className="legend-dot not-sold"></div>
                                        <span>Not Sold</span>
                                    </div>
                                </div>
                                <AuctionChart 
                                    auctionData={filteredData} 
                                    timeframe={timeframe}
                                    allData={completedAuctions}
                                    onDataPointHover={setHoveredAuction}
                                    sortBy={sortBy}
                                    hoveredFilter={hoveredFilter}
                                    showConditionBands={showConditionBands && modelData?.estimator === 'condition'}
                                    conditionBands={conditionBands}
                                    showMileageBands={showMileageBands && modelData?.estimator === 'mileage'}
                                    mileageRegression={mileageRegression}
                                />
                                <div className="stats-row">
                                    <div className="stat-item">
                                        <p className="stat-title">Unit Volume</p>
                                        <p className="stat-value">{filteredData.length}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Sell-Through Rate</p>
                                        <p className="stat-value">{`${safeStats.sellThroughRate}%`}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Average Price</p>
                                        <p className="stat-value">{formatPrice(safeStats.averagePrice)}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">Low</p>
                                        <p className="stat-value">{formatPrice(safeStats.lowestPrice)}</p>
                                    </div>
                                    <div className="stat-item">
                                        <p className="stat-title">High</p>
                                        <p className="stat-value">{formatPrice(safeStats.highestPrice)}</p>
                                    </div>
                                </div>
                                {hasPaidPlan && (
                                <>
                                <hr className="filter-separator" />
                                <div className="filter-section">
                                    {Object.entries(uniqueValues).map(([category, values]) => {
                                        // Get available values for this category in current timeframe
                                        const availableValues = values.filter(value => {
                                            return getTimeframeValues[category]?.has(value);
                                        });
                                        
                                        return availableValues.length > 0 ? (
                                            <div key={category} className="filter-category">
                                                <h3>{categoryDisplayNames[category] || category.replace(/([A-Z])/g, ' $1').trim()}</h3>
                                                {availableValues.map(value => (
                                                    <div key={`${category}-${value}`} className="filter-item">
                                                        <input
                                                            type="checkbox"
                                                            checked={filters[category].has(value)}
                                                            onChange={(e) => handleFilterChange(category, value, e.target.checked)}
                                                            className={isHighlighted(category, value) ? 'highlighted' : ''}
                                                            onMouseEnter={() => setHoveredFilter({ category, value })}
                                                            onMouseLeave={() => setHoveredFilter({ category: null, value: null })}
                                                        />
                                                        <label 
                                                            className={isHighlighted(category, value) ? 'highlighted' : ''}
                                                            onMouseEnter={() => setHoveredFilter({ category, value })}
                                                            onMouseLeave={() => setHoveredFilter({ category: null, value: null })}
                                                        >
                                                            {getDisplayValue(category, value)}
                                                        </label>
                                                    </div>
                                                ))}
                                            </div>
                                        ) : null;
                                    })}
                                </div>
                                </>
                                )}
                            </>
                        )}
                    </div>
                    <div style={{ 
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        width: '95%',
                        marginTop: '2rem',
                        marginBottom: '1rem'
                    }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                            <h2 className="latest-auction-results" style={{ margin: 0, whiteSpace: 'nowrap' }}>Auction Results</h2>
                            <label className="switch">
                                <input
                                    type="checkbox"
                                    checked={auctionViewMode === 'live'}
                                    onChange={(e) => setAuctionViewMode(e.target.checked ? 'live' : 'results')}
                                />
                                <span className="slider round"></span>
                            </label>
                            <h2 className="latest-auction-results" style={{ margin: 0, whiteSpace: 'nowrap' }}>Live Auctions</h2>
                        </div>
                        {auctionViewMode === 'results' && (
                            <select 
                                value={sortOption} 
                                onChange={(e) => setSortOption(e.target.value)}
                                style={{
                                    padding: '8px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    backgroundColor: 'white'
                                }}
                            >
                                <option value="Date: New to Old">Date: New to Old</option>
                                <option value="Date: Old to New">Date: Old to New</option>
                                <option value="Price: High to Low">Price: High to Low</option>
                                <option value="Price: Low to High">Price: Low to High</option>
                                <option value="Miles: High to Low">Miles: High to Low</option>
                                <option value="Miles: Low to High">Miles: Low to High</option>
                            </select>
                        )}
                    </div>
                    {auctionViewMode === 'results' ? (
                    <div className="auction-grid-container" style={{ width: '95%' }}>
                        {!loading && sortedAuctionData.map((auction, index) => {
                            const Wrapper = auction.auctionLink ? 'a' : 'div';
                            return (
                                <Wrapper 
                                    key={index} 
                                    {...(auction.auctionLink ? {
                                        href: auction.auctionLink,
                                        target: "_blank",
                                        rel: "noopener noreferrer"
                                    } : {})}
                                    className="auction-result-tile"
                                >
                                    <img 
                                        src={auction.imageLink || modelData?.imageLink} 
                                        alt={auction.title || `${modelData.modelLine} ${modelData.model}`} 
                                        className={`auction-result-image ${!auction.imageLink ? 'fallback-image' : ''}`} 
                                    />
                                    <div className="auction-result-header">
                                        <h3 className="auction-result-title">
                                            {auction.title || `${modelData.modelLine} ${modelData.model}`}
                                        </h3>
                                        <div className="auction-result-mileage">
                                            {auction.miles ? `${formatMileage(auction.miles)} miles` : 'N/A'}
                                        </div>
                                    </div>
                                    <div className="auction-result-status-row">
                                        <div className="auction-result-status">
                                            <span className={`status-dot ${auction.sold ? 'sold' : 'unsold'}`}></span>
                                            <span>{auction.sold ? 'Sold' : 'Unsold'}</span>
                                        </div>
                                        <span className="auction-result-price">${auction.finalBid.toLocaleString()}</span>
                                    </div>
                                    <div className="auction-details-group">
                                        <div className="auction-result-colors">
                                            <div className="color-row">
                                                <span className="color-label">Exterior Color:</span>
                                                <span className="color-value">{auction.exteriorColor}</span>
                                            </div>
                                            <div className="color-row">
                                                <span className="color-label">Interior Color:</span>
                                                <span className="color-value">{auction.interiorColor}</span>
                                            </div>
                                        </div>
                                        <p className="auction-result-date">
                                            {auction.auctionDate?.seconds ? 
                                                new Date(auction.auctionDate.seconds * 1000).toLocaleDateString('en-US', {
                                                    year: 'numeric',
                                                    month: 'long',
                                                    day: 'numeric'
                                                })
                                                : 'Date not available'
                                            }
                                        </p>
                                    </div>
                                    <div className="auction-result-house">
                                        <span>{auction.auctionHouse}</span>
                                        {auction.auctionLink && (
                                            <svg className="external-link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                            </svg>
                                        )}
                                    </div>
                                </Wrapper>
                            );
                        })}
                    </div>
                    ) : (
                    <div className="auction-grid-container" style={{ width: '95%' }}>
                        {!loading && liveAuctions.length > 0 ? (
                            liveAuctions.map((auction, index) => {
                                const Wrapper = auction.auctionLink ? 'a' : 'div';
                                return (
                                    <Wrapper 
                                        key={index} 
                                        {...(auction.auctionLink ? {
                                            href: auction.auctionLink,
                                            target: "_blank",
                                            rel: "noopener noreferrer"
                                        } : {})}
                                        className="auction-result-tile"
                                    >
                                        <img 
                                            src={auction.imageLink || modelData?.imageLink} 
                                            alt={auction.title || `${modelData.modelLine} ${modelData.model}`} 
                                            className={`auction-result-image ${!auction.imageLink ? 'fallback-image' : ''}`} 
                                        />
                                        <div className="auction-result-header">
                                            <h3 className="auction-result-title">
                                                {auction.title || `${modelData.modelLine} ${modelData.model}`}
                                            </h3>
                                            <div className="auction-result-mileage">
                                                {auction.miles ? `${formatMileage(auction.miles)} miles` : 'N/A'}
                                            </div>
                                        </div>
                                        <div className="auction-details-group">
                                            <div className="auction-result-colors">
                                                <div className="color-row">
                                                    <span className="color-label">Exterior Color:</span>
                                                    <span className="color-value">{auction.exteriorColor}</span>
                                                </div>
                                                <div className="color-row">
                                                    <span className="color-label">Interior Color:</span>
                                                    <span className="color-value">{auction.interiorColor}</span>
                                                </div>
                                            </div>
                                            <p className="auction-result-date">
                                                {auction.auctionDate?.seconds ? 
                                                    new Date(auction.auctionDate.seconds * 1000).toLocaleDateString('en-US', {
                                                        year: 'numeric',
                                                        month: 'long',
                                                        day: 'numeric'
                                                    })
                                                    : 'Date not available'
                                                }
                                            </p>
                                        </div>
                                        <div className="auction-result-house">
                                            <span>{auction.auctionHouse}</span>
                                            {auction.auctionLink && (
                                                <svg className="external-link-icon" width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M15 3h6v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M10 14L21 3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                            )}
                                        </div>
                                    </Wrapper>
                                );
                            })
                        ) : (
                            <p style={{ color: '#666', fontSize: '16px', gridColumn: '1 / -1' }}>No live auctions at this time</p>
                        )}
                    </div>
                    )}
                </section>
            </main>

            {showEstimate && (
                <Estimate onClose={handleClose} modelId={modelId} />
            )}
            {!hasPaidPlan && <AdvertisePopup />}
            <Footer />
        </div>
    );
};

export default CarPage;
